import { AddressResponse } from '../api/legacy/getAddressHistory';
import { Currency, Coupon, PaymentStatus } from './payment';

export interface Country {
  alpha2: string;
  continent: string; // continentCode?
  enable: boolean;
  hot: boolean;
  name: Record<string, string>;
  postalCodeSample: string;
  requirePostalCode: boolean;
  zoneName: string;
}

export interface City {
  name: Record<string, string>;
  district: string;
  parent?: number;
  country: Country['alpha2'];
  enable: boolean;
  slug: string;
}

export interface State {
  name: string;
  code: string;
}

export enum AddressType {
  RESIDENTIAL = 'RESIDENTIAL',
  BUSINESS = 'BUSINESS',
}

export enum CardType {
  Mastercard = 'Mastercard',
}

export interface Address {
  name: string;
  companyName?: string;
  phone: string;
  dialCode: string;
  street: string;
  city: string;
  state?: string;
  postalCode?: string;
  type: AddressType;
  metadata?: Metadata;
}

export interface Metadata {
  halId: string;
}

export enum ShipmentType {
  DOCUMENT = 'document',
  PARCEL = 'parcel',
}
export enum InvoiceType {
  PERSONAL = 'PERSONAL',
  COMPANY = 'COMPANY',
}

export enum CarrierType {
  CARRIER_NORMAL_INVOICE = 1,
  CARRIER_PIN = 2,
  CARRIER_MOBILE_BARCODE = 3,
}

export enum TaxInfoInputError {
  CARRIER_PIN = 'Invoice.MONICACarrierNumberRequired',
  CARRIER_MOBILE_BARCODE = 'Invoice.mobileCarrierNumberRequired',
  COMPANY = 'Invoice.companyInformationIncompleted',
  CAN_CREDIT_NOTE = 'Invoice.canCreditNoteRequired',
}

export interface Parcel {
  length: number;
  width: number;
  height: number;
  weight: number;
}

export interface Item {
  description: string;
  value: Currency;
  qty: number;
  hkhsCode?: string;
  isIncludesFood?: boolean;
  isIncludesBattery?: boolean;
  countryOfManufacture?: string;
  remarks?: string;
}

export enum ItemType {
  PERSONAL_USE = 'personal_use',
  COMMERCIAL_USE = 'commercial_use',
  RETURN_GOODS = 'return_goods',
  SAMPLE_USE = 'sample_use',
}

export enum Incoterm {
  DDU = 'DDU',
  DDP = 'DDP',
}

export interface Shipment {
  originCountry: string;
  originAddress: Address;
  destinationCountry: string;
  destinationAddress: Address;
  holdAtLocation: HoldAtLocation | null;
  type: ShipmentType;
  parcels: Parcel[];
  parcelsInfo: Item[] | Item[][];
  items: Item[];
  service: string;
  itemType: ItemType;
  incoterm: Incoterm;
}

export interface Service {
  name: Record<string, string>;
  carrier: string;
  carrierName: Record<string, string>;
  slug: string;
  enable: boolean;
  firstMileProvider?: string;
  noService: boolean;
  noTracking: boolean;
  maxDeliveryDays: number;
  minDeliveryDays: number;
  totalRate: Currency;
  // amounts inside naturalAmount are in HKD
  naturalAmount: Record<string, number>;
}

export interface HoldAtLocation {
  locationId: string;
  address: Omit<Address, 'street' | 'city' | 'state' | 'name' | 'phone' | 'dialCode' | 'type'>;
}

export enum CollectMethod {
  PICKUP = 'pickup',
  DROPOFF = 'dropoff',
}

export interface TimeInterval {
  from: Date;
  to: Date;
}

export interface PickupTime {
  date: string;
  from: string;
  to: string;
}

export interface CollectShipment {
  method: CollectMethod;
  time?: PickupTime;
}

export enum OrderStatus {
  ORDER_VOID = -2,
  ORDER_CANCEL = -1,
  ORDER_PENDING = 0,
  ORDER_SUCCESS = 1,
  ORDER_COMPLETE = 2,
  ORDER_PAYING = 3,
  ORDER_HOLD = 4,
  ORDER_RETURN = 5,
}

export interface CustomItem {
  currency: string;
  description: string;
  quantity: number;
  value: number;
}

export interface Pickup {
  address: AddressResponse;
  minDatetime: string;
  maxDatetime: string;
  pickupNumber?: string;
  firstMileProvider: {
    slug: string;
  } | null;
  type: 'NORMAL' | 'SELF_PICKUP' | 'FIRST_MILE';
}
export interface Order {
  ref: string;
  discount: Currency;
  totalCharges?: Currency;
  returnFee?: Currency;
  finalTotal: Currency;
  chargeDifference?: Currency;
  paymentStatus: PaymentStatus;
  labelUrl?: string;
  invoice: {
    url: string;
  } | null;
  pickup?: Pickup;
  shipments: {
    ref: string;
    carrierService: { carrier: string; carrierName: Record<'en' | 'zh', string> };
    fromAddress: AddressResponse;
    isPaperless: boolean | number;
    shipmentRef: string;
    toAddress: AddressResponse;
    trackingNumber: string;
    tracking?: {
      checkpoints: {
        city?: string;
        country?: string;
        location?: string;
        state?: string;
        tag: string;
        message: string;
        checkpointTime: string;
      }[];
      trackingNumber: string;
    };
    taxInfo?: TaxInfo;
    boxes: Parcel[];
    originalBoxes: Parcel[] | null;
    customsItems: CustomItem[];
    totalWeight: number;
    previewWeight: number;
    deliveryDate: string;
  }[];
  coupon: Coupon | null;
  status: OrderStatus;
  // amounts inside naturalAmount are in HKD
  createdAt?: string;
  updatedAt?: string;
  naturalAmount: Record<string, number>;
}

export interface TaxInfo {
  type: 'PERSONAL' | 'COMPANY';
  invoiceCarrierId?: CarrierType;
  invoiceCarrierNumber?: string;
  companyName?: string;
  companyAddress?: string;
  companyVatNumber?: string;
  canCreditNote: boolean;
}

export interface CouponError {
  code: string;
  message: string;
  couponCode?: string;
}

export interface Checkpoint {
  message: string;
  location: string;
  time: string;
}

export enum OrderFilter {
  ON_GOING = 'ongoing',
  PAST = 'past',
}

export enum ShipmentStatus {
  TO_BE_COLLECTED = 'TO_BE_COLLECTED',
  ON_THE_WAY = 'ON_THE_WAY',
  ACTION_REQUIRED = 'ACTION_REQUIRED',
  ON_HOLD = 'ON_HOLD',
  CANCELLED = 'CANCELLED',
  DELIVERED = 'DELIVERED',
}

export enum Channel {
  WhatsApp = 'whatsapp',
  Email = 'email',
  Line = 'line',
  Facebook = 'facebook',
  CodeCopied = 'code_copied',
}
